import {
  defaultConfirmationDialogButtonsText,
  FORM_PROMPT_DISCARD_MESSAGE
} from "common/ui-components/form/Form.types";
import { ReactNode } from "react";
import {
  FieldValues,
  FormState,
  useFormContext,
  UseFormReturn
} from "react-hook-form";

import {
  confirm,
  DefaultButton,
  IButtonProps,
  IContextualMenuItem,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  SplitButton
} from "@bps/fluent-ui";

interface ISplitButtonItems<FormValues extends FieldValues>
  extends IContextualMenuItem {
  onMenuItemClick?: (
    form: UseFormReturn<FormValues>,
    item?: IContextualMenuItem
  ) => void;
}

export interface FormSubmitCancelButtonsProps<FormValues extends FieldValues> {
  onCancel?: (form: UseFormReturn<FormValues>) => void;
  disableCancel?: boolean;
  disableSubmit?: (formState: FormState<FormValues>) => boolean;
  submitButtonProps?: IButtonProps;
  cancelButtonProps?: IButtonProps;
  submitDataTestAttr?: string;
  cancelDataTestAttr?: string;
  formId?: string;
  className?: string;
  onRenderSubmitCancelButtons?: (form: UseFormReturn<FormValues>) => ReactNode;
  extraActionsBefore?: (form: UseFormReturn<FormValues>) => ReactNode;
  items?: ISplitButtonItems<FormValues>[];
}

export const FormSubmitCancelButtons = <
  FormValues extends FieldValues = FieldValues
>({
  onCancel,
  disableSubmit,
  disableCancel,
  submitButtonProps,
  cancelButtonProps,
  submitDataTestAttr,
  cancelDataTestAttr,
  formId,
  className,
  onRenderSubmitCancelButtons,
  extraActionsBefore,
  items
}: FormSubmitCancelButtonsProps<FormValues>) => {
  const form = useFormContext<FormValues>();

  const onRenderIcon = (props, defaultRender) => {
    if (form.formState.isSubmitting) {
      return <Spinner size={SpinnerSize.small} />;
    }

    return defaultRender ? defaultRender(props) : null;
  };

  const submitButtonCommonProps: IButtonProps = {
    disabled: disableSubmit
      ? disableSubmit(form.formState)
      : form.formState.isSubmitting || !form.formState.isDirty,
    iconProps: {
      iconName: submitButtonProps?.iconProps?.iconName || "Save"
    },
    text: submitButtonProps?.text || "Save",
    primary: true,
    type: "submit",
    form: formId,
    onRenderIcon: submitButtonProps?.onRenderIcon || onRenderIcon
  };

  const getPrimaryButton = () => {
    if (items && items.length > 0) {
      const newItems = items.map(i => {
        const { onMenuItemClick, onClick } = i;
        const newItem = { ...i };
        newItem.onClick = (_e, item) => {
          onClick && onClick();
          onMenuItemClick && onMenuItemClick(form, item);
        };
        return newItem;
      });
      return (
        <SplitButton
          items={newItems}
          {...submitButtonCommonProps}
          onRenderIcon={submitButtonProps?.onRenderIcon}
        />
      );
    }
    return (
      <PrimaryButton
        data-test={submitDataTestAttr ?? "save-button"}
        {...submitButtonCommonProps}
        {...submitButtonProps}
      />
    );
  };
  return (
    <div className={className}>
      {onRenderSubmitCancelButtons ? (
        onRenderSubmitCancelButtons(form)
      ) : (
        <>
          {extraActionsBefore ? extraActionsBefore(form) : null}
          {getPrimaryButton()}
          <DefaultButton
            text="Cancel"
            data-test={cancelDataTestAttr ?? "cancel-button"}
            styles={{ root: { marginLeft: 16 } }}
            {...cancelButtonProps}
            disabled={disableCancel}
            onClick={() => {
              if (onCancel) {
                onCancel(form);
              } else {
                confirm({
                  dialogContentProps: { subText: FORM_PROMPT_DISCARD_MESSAGE },
                  confirmButtonProps: {
                    text: defaultConfirmationDialogButtonsText.discard
                  },
                  cancelButtonProps: {
                    text: defaultConfirmationDialogButtonsText.cancel
                  }
                }).then(reset => {
                  if (reset) {
                    form.reset();
                  }
                });
              }
            }}
          />
        </>
      )}
    </div>
  );
};
